/* eslint-disable indent */
import axios, { AxiosError } from 'axios';
import toast from 'react-hot-toast';

import { ApiCall } from '../api/api-call';
import { CustomError, PageResponse } from '../types/api.type';
import { logout } from '../lib/utils';
import { getTokenClient } from '../lib/auth.client';
import { refreshTokenService } from '../lib/refresh-token';
import { triggerSessionExpired } from '../lib/session-expired.service';

import { API_BASE_URL } from '@/slw/shared/consts/common.const';

// Global error throttling singleton
class ErrorThrottler {
  private static instance: ErrorThrottler;
  private hasShownError = false;
  private errorTimeout: NodeJS.Timeout | null = null;
  private readonly THROTTLE_MS = 5000; // 5 seconds

  private constructor() {}

  public static getInstance(): ErrorThrottler {
    if (!ErrorThrottler.instance) {
      ErrorThrottler.instance = new ErrorThrottler();
    }
    return ErrorThrottler.instance;
  }

  public showError(message: string): void {
    // Always skip if already showing an error
    if (this.hasShownError) {
      return;
    }
    
    this.hasShownError = true;
    toast.error(message);
    
    // Clear any existing timeout
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
    }
    
    // Reset the flag after timeout
    this.errorTimeout = setTimeout(() => {
      this.hasShownError = false;
    }, this.THROTTLE_MS);
  }
}

// Function to show error message with throttling
const showErrorWithThrottling = (message: string) => {
  const throttler = ErrorThrottler.getInstance();
  throttler.showError(message);
};

const handleApiError = (error: AxiosError<PageResponse<unknown>>, errorHandling?: CustomError) => {
  const handling = errorHandling || 'throwAndNotify';

  if (!axios.isAxiosError(error)) {
    const errorMessage = (error as Error).message || 'Something went wrong';
    
    if (handling === 'throwAndNotify') {
      showErrorWithThrottling(errorMessage);
    }

    return Promise.reject(error);
  }

  let errorMessage = error.message;

  if (error.response) {
    const status = error.response.status;
    const responseData = error.response.data;

    switch (status) {
      case 400:
        errorMessage = responseData?.message || 'Bad Request';
        break;
      case 401:
        errorMessage = 'Session expired. Please login again.';
        logout(false); // Don't redirect automatically
        // Trigger session expired dialog for web
        triggerSessionExpired();
        // Don't show toast for 401 - dialog will handle it
        return Promise.reject(new AxiosError(errorMessage, error.code, error.config, error.request, error.response));
      case 403:
        errorMessage = 'Forbidden';
        break;
      case 404:
        errorMessage = responseData?.message || 'Not Found';
        break;
      case 500:
      case 502:
      case 503:
        errorMessage = 'Server Error';
        break;
      default:
        errorMessage = responseData?.message || 'Unknown Error';
    }
  } else if (error.request) {
    errorMessage = 'Connection timeout. Please check your internet connection and try again.';
  } else {
    // Network error or other issues
    errorMessage = error.message || 'Network error occurred';
  }

  if (handling === 'throwAndNotify') {
    // Don't show toast for 401 errors - auth modal will handle it
    if (error.response?.status !== 401) {
      showErrorWithThrottling(errorMessage);
    }
  }

  return Promise.reject(error);
};
const api = axios.create({ 
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds timeout
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const errorHandling = error.config?.errorHandling as CustomError;

    return handleApiError(error, errorHandling);
  },
);

async function attachApiInterceptor() {
  api.interceptors.request.use(
    async (config) => {
      // Get valid access token (will refresh if needed)
      const token = await refreshTokenService.getValidAccessToken();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
    (error) => Promise.reject(error),
  );

  // Add response interceptor for token refresh
  api.interceptors.response.use(
    (response) => {
      // Check for new tokens in response headers
      const newAccessToken = response.headers['x-new-access-token'];
      const newRefreshToken = response.headers['x-new-refresh-token'];
      const tokenExpiresAt = response.headers['x-token-expires-at'];

      if (newAccessToken && newRefreshToken && tokenExpiresAt) {
        // Update tokens in localStorage
        localStorage.setItem('auth_client', newAccessToken);
        localStorage.setItem('refresh_token', newRefreshToken);
        localStorage.setItem('token_expires_at', tokenExpiresAt);
      }

      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // If error is 401 and we haven't already tried to refresh
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          // Try to refresh the token
          const refreshResult = await refreshTokenService.refreshAccessToken();
          
          if (refreshResult.success && refreshResult.data) {
            // Update the authorization header with new token
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${refreshResult.data.accessToken}`;
            }

            // Retry the original request
            return api(originalRequest);
          } else {
            // Refresh failed, logout user
            logout(false);
            triggerSessionExpired();
            return Promise.reject(error);
          }
        } catch (refreshError) {
          // Refresh failed, logout user
          logout(false);
          triggerSessionExpired();
          return Promise.reject(error);
        }
      }

      return Promise.reject(error);
    }
  );
}

const apiCall = new ApiCall(api, attachApiInterceptor);

// Export apiClient function
export const apiClient = () => apiCall;

if (typeof window !== 'undefined') {
  window.apiClient = () => apiCall;
} else {
  global.apiClient = () => apiCall;
}
